// ReportWeekdayWeekend.jsx

import React, { useMemo } from "react";
import { Container, Typography } from "@mui/material";
import HoverPaper from "../HoverPaper";
import Colours from "../../../helpers/colours";
import EChartsReact from "echarts-for-react";

const weekDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
const weekEnd = ["Saturday", "Sunday"];

const METRICS = {
    "Total Price": "revenueTotal",
    "Booking Total": "bookingsTotal",
    "Room Nights": "roomNightsTotal",
};

const METRICS_TITLE = {
    "Total Price": "Revenue",
    "Booking Total": "Bookings",
    "Room Nights": "Room Nights",
};

export const REPORT_WEEKDAY_WEEKEND = "REPORT_WEEKDAY_WEEKEND";
const ReportWeekdayWeekend = ({ data, secondaryData, js, metric }) => {
    const getGroupedData = useMemo(
        () => (sourceData) =>
            Object.values(sourceData || {}).reduce(
                (acc, item) => {
                    const keyMap = Object.fromEntries(item.keys || []);
                    const day = new Date(
                        Date.UTC(
                            Number(keyMap.Year),
                            Number(keyMap.Month) - 1,
                            Number(keyMap.Day),
                        ),
                    ).toLocaleDateString("en-US", {
                        weekday: "long",
                        timeZone: "UTC",
                    });

                    const group = weekDays.includes(day)
                        ? "Weekdays"
                        : weekEnd.includes(day)
                          ? "Weekends"
                          : null;

                    if (!group) {
                        return acc;
                    }

                    acc[group] += item.result?.[METRICS[metric]] || 0;

                    return acc;
                },
                {
                    Weekdays: 0,
                    Weekends: 0,
                },
            ),
        [metric],
    );

    const checkinData = useMemo(
        () => getGroupedData(data),
        [data, getGroupedData],
    );
    const bookingData = useMemo(
        () => getGroupedData(secondaryData),
        [secondaryData, getGroupedData],
    );

    const formatValue = (value) =>
        js?.numberWithCommas ? js.numberWithCommas(value) : value;

    const option = useMemo(
        () => ({
            tooltip: {
                trigger: "axis",
                axisPointer: {
                    type: "shadow",
                },
                formatter: (params) => {
                    const lines = params.map(
                        (item) =>
                            `${item.marker} ${item.seriesName}: ${formatValue(item.value)}`,
                    );

                    return [params?.[0]?.axisValue, ...lines].join("<br/>");
                },
            },
            legend: {
                data: ["Bookings", "Check-in"],
            },
            grid: {
                left: 80,
                right: 30,
                top: 50,
                bottom: 40,
            },
            xAxis: {
                type: "category",
                data: ["Weekdays (Mon-Fri)", "Weekends (Sat-Sun)"],
            },
            yAxis: {
                type: "value",
                name: METRICS_TITLE[metric],
            },
            series: [
                {
                    name: "Bookings",
                    type: "bar",
                    data: [bookingData.Weekdays, bookingData.Weekends],
                },
                {
                    name: "Check-in",
                    type: "bar",
                    data: [checkinData.Weekdays, checkinData.Weekends],
                },
            ],
        }),
        [bookingData, checkinData, metric],
    );

    return (
        <>
            <Typography fontWeight={"bold"} sx={{ pt: 2 }}>
                Booking vs Check-in {METRICS_TITLE[metric]}: Weekday vs Weekend
            </Typography>

            <Container
                maxWidth={false}
                component={HoverPaper}
                sx={{ p: 2, backgroundColor: Colours.notificationCard }}
            >
                <EChartsReact
                    style={{ height: 300, width: "100%" }}
                    option={option}
                />
            </Container>
        </>
    );
};

export default ReportWeekdayWeekend;
